"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Pencil,
  Trash2,
  Plus,
  MoreVertical,
  ChevronDown,
  Loader2,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ConfirmDialog from "@/components/admin/shared/confirm-dialog";
import { toast } from "sonner";
import { formatNumber } from "@/lib/utils";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { PRICE_TYPE_IDS } from "@/lib/constants/price-types";

interface AddOn {
  id: number;
  venue_id: number;
  name: string;
  description?: string;
  add_on_type: string;
  pricing_type?: string;
  price_type_id?: number;
  customer_type_id?: number | null;
  customer_type?: {
    id: number;
    name: string;
  } | null;
  pricing_id: number;
  pricing: {
    price: number;
    vat_percentage: number;
  };
  status_id: number;
  status?: {
    id: number;
    name: string;
    type: string;
  };
  article_code?: string | null;
  created_at: string;
  updated_at: string;
}

interface AddOnsPageClientProps {
  venueId: number;
  venueName: string;
  userRole: string;
  venueCurrency: string;
}

export default function AddOnsPageClient({
  venueId,
  venueName,
  userRole,
  venueCurrency,
}: AddOnsPageClientProps) {
  const router = useRouter();
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [addOnToDelete, setAddOnToDelete] = useState<AddOn | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    customerTypeId: "",
    isOnRoad: false,
  });
  const [customerTypes, setCustomerTypes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [loadingCustomerTypes, setLoadingCustomerTypes] = useState(true);

  const isSuperAdmin = userRole === "super_admin";
  const isBookingAdmin = userRole === "booking_staff";
  const canModify = isSuperAdmin || isBookingAdmin;

  const fetchAddOns = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/venues/${venueId}/add-ons`);
      if (!response.ok) throw new Error("Failed to fetch add-ons");
      const data = await response.json();
      setAddOns(data);
    } catch (error) {
      toast.error("Failed to load add-ons");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => {
    fetchAddOns();
    fetchCustomerTypes();
  }, [fetchAddOns]);

  const fetchCustomerTypes = async () => {
    try {
      const response = await fetch("/api/customer-types");
      if (response.ok) {
        const types = await response.json();
        setCustomerTypes(types);
        // Set the first customer type as default if available
        if (types.length > 0) {
          setFormData((prev) => ({
            ...prev,
            customerTypeId: types[0].id.toString(),
          }));
        }
      }
    } catch (error) {
    } finally {
      setLoadingCustomerTypes(false);
    }
  };

  const categories = [
    { value: "all", label: "All", count: addOns.length },
    {
      value: "food",
      label: "Food",
      count: addOns.filter((a) => a.add_on_type === "food").length,
    },
    {
      value: "drink",
      label: "Drinks",
      count: addOns.filter((a) => a.add_on_type === "drink").length,
    },
    {
      value: "service",
      label: "Services",
      count: addOns.filter((a) => a.add_on_type === "service").length,
    },
    {
      value: "equipment",
      label: "Equipment",
      count: addOns.filter((a) => a.add_on_type === "equipment").length,
    },
    {
      value: "other",
      label: "Other",
      count: addOns.filter((a) => a.add_on_type === "other").length,
    },
  ];

  // Count add-ons by status
  const statusCounts = {
    all: addOns.length,
    available: addOns.filter((a) => a.status_id === STATUS_IDS.ADD_ON_AVAILABLE)
      .length,
    unavailable: addOns.filter(
      (a) => a.status_id === STATUS_IDS.ADD_ON_UNAVAILABLE,
    ).length,
    discontinued: addOns.filter(
      (a) => a.status_id === STATUS_IDS.ADD_ON_DISCONTINUED,
    ).length,
  };

  const filteredAddOns = addOns.filter((addon) => {
    const categoryMatch =
      selectedCategory === "all" || addon.add_on_type === selectedCategory;
    const statusMatch =
      selectedStatus === "all" ||
      (selectedStatus === "available" &&
        addon.status_id === STATUS_IDS.ADD_ON_AVAILABLE) ||
      (selectedStatus === "unavailable" &&
        addon.status_id === STATUS_IDS.ADD_ON_UNAVAILABLE) ||
      (selectedStatus === "discontinued" &&
        addon.status_id === STATUS_IDS.ADD_ON_DISCONTINUED);
    return categoryMatch && statusMatch;
  });

  const handleDelete = async () => {
    if (!addOnToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(
        `/api/venues/${venueId}/add-ons/${addOnToDelete.id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete add-on");
      }

      toast.success("Add-on deleted successfully");

      await fetchAddOns();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete add-on");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setAddOnToDelete(null);
    }
  };

  const handleEdit = (addOn: AddOn) => {
    const portal = userRole === "super_admin" ? "superadmin" : "booking";
    router.push(`/${portal}/venue/${venueId}/add-ons/${addOn.id}`);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      customerTypeId:
        customerTypes.length > 0 ? customerTypes[0].id.toString() : "",
      isOnRoad: false,
    });
  };

  const handleAddAddon = async () => {
    if (!formData.name) return;

    try {
      // Create add-on without pricing
      const response = await fetch(`/api/venues/${venueId}/add-ons`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          add_on_type: "other", // Default type
          customer_type_id: parseInt(formData.customerTypeId),
          price_type_id: PRICE_TYPE_IDS.FIXED, // Default to FIXED pricing type
          is_on_road: formData.isOnRoad, // Road-show add-on discriminator
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create add-on");
      }

      const newAddOn = await response.json();

      toast.success("Add-on created successfully");
      setIsAddDialogOpen(false);
      resetForm();

      // Refresh the add-ons list
      await fetchAddOns();

      // Navigate to the add-on details page
      const portal = userRole === "super_admin" ? "superadmin" : "booking";
      const editUrl = `/${portal}/venue/${venueId}/add-ons/${newAddOn.id}`;

      // Ensure we have valid IDs
      if (!newAddOn.id) {
        throw new Error("Add-on created but no ID returned");
      }

      router.push(editUrl);
    } catch (error: any) {
      toast.error(error.message || "Failed to create add-on");
    }
  };

  const getAddOnTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      food: "Food",
      drink: "Drink",
      service: "Service",
      equipment: "Equipment",
      other: "Other",
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Add-ons management</h1>
            <p className="text-gray-600 mt-2">
              Manage venue add-ons and extras for {venueName}
            </p>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => resetForm()}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add new add-on
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader className="pb-4">
                <DialogTitle className="text-xl font-semibold">
                  Add new add-on
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Enter a name and customer type for the new add-on. You can
                  configure additional details after creation.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Add-on name *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        name: e.target.value,
                      })
                    }
                    placeholder="e.g., Welcome Drink, Photo Package"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    You can add translations and other details in the edit page
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="isOnRoad"
                    checked={formData.isOnRoad}
                    onCheckedChange={(checked) =>
                      setFormData({
                        ...formData,
                        isOnRoad: checked === true,
                      })
                    }
                  />
                  <Label
                    htmlFor="isOnRoad"
                    className="text-sm font-normal text-gray-700"
                  >
                    Road show add-on
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="customerType"
                    className="text-sm font-medium text-gray-700"
                  >
                    Customer type *
                  </Label>
                  <select
                    id="customerType"
                    value={formData.customerTypeId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customerTypeId: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={loadingCustomerTypes}
                  >
                    {customerTypes.map((type) => (
                      <option key={type.id} value={type.id.toString()}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    Choose who can purchase this add-on
                  </p>
                </div>
              </div>

              <DialogFooter className="pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                  className="mr-2"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddAddon}
                  disabled={!formData.name || !formData.customerTypeId}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create add-on
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-start">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by type
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.value}
                    onClick={() => setSelectedCategory(category.value)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedCategory === category.value
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {category.label} ({category.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by status
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[200px] justify-between"
                  >
                    {selectedStatus === "all"
                      ? "All"
                      : selectedStatus.charAt(0).toUpperCase() +
                        selectedStatus.slice(1)}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[200px]">
                  <DropdownMenuItem
                    onClick={() => setSelectedStatus("all")}
                    className={selectedStatus === "all" ? "bg-gray-100" : ""}
                  >
                    <span className="flex items-center justify-between w-full">
                      <span>All</span>
                      <span className="text-gray-500 text-sm">
                        {statusCounts.all}
                      </span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSelectedStatus("available")}
                    className={
                      selectedStatus === "available" ? "bg-gray-100" : ""
                    }
                  >
                    <span className="flex items-center justify-between w-full">
                      <span>Available</span>
                      <span className="text-gray-500 text-sm">
                        {statusCounts.available}
                      </span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSelectedStatus("unavailable")}
                    className={
                      selectedStatus === "unavailable" ? "bg-gray-100" : ""
                    }
                  >
                    <span className="flex items-center justify-between w-full">
                      <span>Unavailable</span>
                      <span className="text-gray-500 text-sm">
                        {statusCounts.unavailable}
                      </span>
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setSelectedStatus("discontinued")}
                    className={
                      selectedStatus === "discontinued" ? "bg-gray-100" : ""
                    }
                  >
                    <span className="flex items-center justify-between w-full">
                      <span>Discontinued</span>
                      <span className="text-gray-500 text-sm">
                        {statusCounts.discontinued}
                      </span>
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Add-ons Table */}
        {filteredAddOns.length > 0 ? (
          <div className="bg-white rounded-lg border shadow overflow-hidden">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow className="border-b-2">
                  <TableHead className="text-gray-700 font-semibold w-1/4 px-6 py-4">
                    Add-on name
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                    Category
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                    Type
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                    Customer type
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                    Price ({getCurrencySymbol(venueCurrency)})
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                    VAT
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/6 px-6 py-4">
                    Accounting article
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/8 px-6 py-4">
                    Status
                  </TableHead>
                  {canModify && (
                    <TableHead className="text-right text-gray-700 font-semibold px-6 py-4">
                      Actions
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAddOns.map((addon) => (
                  <TableRow key={addon.id} className="hover:bg-gray-50">
                    <TableCell className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {addon.name}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          {addon.description || "No description"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <Badge
                        variant="secondary"
                        className={`${
                          addon.add_on_type === "food"
                            ? "bg-green-100 text-green-800 border-green-200"
                            : addon.add_on_type === "drink"
                              ? "bg-blue-100 text-blue-800 border-blue-200"
                              : addon.add_on_type === "service"
                                ? "bg-purple-100 text-purple-800 border-purple-200"
                                : addon.add_on_type === "equipment"
                                  ? "bg-orange-100 text-orange-800 border-orange-200"
                                  : "bg-gray-100 text-gray-800 border-gray-200"
                        }`}
                      >
                        {getAddOnTypeLabel(addon.add_on_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <Badge
                        variant="secondary"
                        className={`${
                          addon.pricing_type === "per_person"
                            ? "bg-blue-100 text-blue-800 border-blue-200"
                            : "bg-gray-100 text-gray-800 border-gray-200"
                        }`}
                      >
                        {addon.pricing_type === "per_person"
                          ? "Per Person"
                          : "Individual"}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <Badge
                        variant="secondary"
                        className={`${
                          addon.customer_type?.name === "Corporate"
                            ? "bg-purple-100 text-purple-800 border-purple-200"
                            : "bg-teal-100 text-teal-800 border-teal-200"
                        }`}
                      >
                        {addon.customer_type?.name || "Not Set"}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {addon.pricing ? (
                        <div className="font-medium text-gray-900">
                          {formatNumber(addon.pricing.price)}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">
                          No pricing set
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {addon.pricing ? (
                        <div className="font-medium text-gray-900">
                          {addon.pricing.vat_percentage}%
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      {addon.customer_type?.name?.toLowerCase() ===
                      "corporate" ? (
                        addon.article_code ? (
                          <Badge
                            variant="secondary"
                            className="bg-blue-100 text-blue-800 border-blue-200"
                          >
                            {addon.article_code}
                          </Badge>
                        ) : (
                          <Badge
                            variant="secondary"
                            className="bg-amber-100 text-amber-800 border-amber-200"
                          >
                            Not mapped
                          </Badge>
                        )
                      ) : (
                        <span className="text-gray-400 italic">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <Badge
                        variant="secondary"
                        className={`${
                          addon.status_id === STATUS_IDS.ADD_ON_AVAILABLE
                            ? "bg-green-100 text-green-800 border-green-200"
                            : addon.status_id === STATUS_IDS.ADD_ON_UNAVAILABLE
                              ? "bg-red-100 text-red-800 border-red-200"
                              : addon.status_id ===
                                  STATUS_IDS.ADD_ON_DISCONTINUED
                                ? "bg-gray-100 text-gray-800 border-gray-200"
                                : "bg-gray-100 text-gray-800 border-gray-200"
                        }`}
                      >
                        {addon.status_id === STATUS_IDS.ADD_ON_AVAILABLE
                          ? "Available"
                          : addon.status_id === STATUS_IDS.ADD_ON_UNAVAILABLE
                            ? "Unavailable"
                            : addon.status_id === STATUS_IDS.ADD_ON_DISCONTINUED
                              ? "Discontinued"
                              : "Unknown"}
                      </Badge>
                    </TableCell>
                    {canModify && (
                      <TableCell className="text-right px-6 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(addon)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setAddOnToDelete(addon);
                                setShowDeleteDialog(true);
                              }}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          /* Empty State */
          <div className="bg-white rounded-lg border shadow overflow-hidden py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Plus className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No add-ons yet
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Get started by creating your first add-on. Add-ons are extras
                that customers can purchase with their bookings.
              </p>
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add your first add-on
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete add-on"
        description="Are you sure you want to delete this add-on? Add-ons that are linked to active or future bookings cannot be deleted."
        itemName={addOnToDelete?.name}
        itemSubtext={`Type: ${addOnToDelete?.add_on_type}`}
        confirmText="Delete"
        confirmLoadingText="Deleting..."
        isLoading={isDeleting}
      />
    </div>
  );
}
